<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Notifications</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :list-options="list_options"
      :new-to="{ name: 'notification.create', params: { id: 0 } }"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      @filter_changed="update_filter($event)"
      @options_changed="update_table($event)"
      :schema="schema"
      :query-select-options="options"
      @reset_table="reset_table"
      new-button-text="New Notification"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <BTable
      :fields="fields"
      :items="items"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
      @row-selected="onRowSelected"
      @sort-changed="change_sort"
      bordered
      class="o-list"
      head-variant="light"
      no-local-sorting
      :selectable="selected.selectable"
      :key="selected.selectable"
      striped
      thead-class="o-gradient-header"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'active_end_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_end_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'active_start_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_start_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'buttons'">
            <BFormInput
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.buttons"
            ></BFormInput>
          </span>
          <span v-if="field.key === 'description'">
            <BFormInput
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.description"
            ></BFormInput>
          </span>
          <span v-if="field.key === 'statuses'">
            <BFormInput
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.statuses"
            ></BFormInput>
          </span>
        </td>
      </template>
      <template #cell(active_end_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_end_date'"
        >
          <date-display
            :value="item.active_end_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(active_start_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_start_date'"
        >
          <date-display
            :value="item.active_start_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(buttons)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'buttons'"
        >
          <BButton
            v-if="btn"
            :href="btn.url ? btn.url : '#'"
            class="mt-3"
            v-for="btn in item.buttons"
            :key="btn.id"
            block
            :variant="btn.style"
            @click="click_button(dialog_notification, btn)"
          >
            {{ btn.button_text }}
          </BButton>
          <h4
            class="text-center text-secondary"
            v-if="item.buttons.length === 0"
          >
            No button
          </h4>
          <!-- <default-display :value="item.buttons"></default-display> -->
        </o-link-cell>
      </template>
      <template #cell(created_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(description)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'description'"
          style="max-width: 400px"
        >
          <default-display :value="item.description"></default-display>
        </o-link-cell>
      </template>
      <template #cell(statuses)="{ index, item }">
        <o-link-cell
          :to="{ name: 'notification.edit', params: { id: item.id } }"
          :when="first_column_key === 'statuses'"
        >
          <h6 v-for="status in displayStatuses(item)" :key="status.id">
            - {{ getUsername(status.user) }}
            <span :class="`text-${readStatusColor(status)}`"
              >({{ getReadStatus(status) }})</span
            >
          </h6>
          <a href="#" @click="showAllStatuses()">{{ statuses_text }}</a>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'notification.create' }"
          >create a new item</router-link
        >
      </template>
    </BTable>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import { BButton, BFormInput, BTable } from "bootstrap-vue";
import _ from "lodash";
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils.js";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify.js";
import schema from "../../schema/index.js";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar.vue";
import OListPaging from "../../../vue-ocom/components/list/OListPaging.vue";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay.vue";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay.vue";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay.vue";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay.vue";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay.vue";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad.vue";
import EmailIcon from "vue-material-design-icons/Email.vue";
import LinkIcon from "vue-material-design-icons/Link.vue";
import moment from "moment";

export default {
  components: {
    BTable,
    BFormInput,
    BButton,
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Notification", "notification"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("Notification"),
    CreateCodeTableFetchMixin(["User"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.Notification,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Notification),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },

      statuses: [],
      show_all_statuses: false,
      statuses_text: "See more...",
    };
  },
  methods: {
    displayStatuses(item) {
      if (this.show_all_statuses) return item.statuses;
      else return item.statuses.slice(0, 5);
    },
    showAllStatuses() {
      if (this.show_all_statuses) {
        this.statuses_text = "See more...";
        this.show_all_statuses = false;
      } else {
        this.statuses_text = "Hide";
        this.show_all_statuses = true;
      }
    },
  },
  computed: {
    options() {
      return {};
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "description",
        "created_date",
        "statuses",
        "buttons",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    getUsername() {
      return (id) => _.find(this.optionUserItems, { id: id }).username;
    },
    getReadStatus() {
      return (status) => {
        if (status && status.read) {
          return moment(status.read).format("MMM Do YY, h:mm:ss a");
        }

        return "Unread";
      };
    },
    readStatusColor() {
      return (status) => (status.read ? "success" : "danger");
    },
  },
};
</script>
